/* eslint-disable react-refresh/only-export-components -- come ActiveMatchContext: il provider
   e il suo hook vivono nello stesso modulo perche' sono un'unita' sola. */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import { env } from '@/env';
import { qgammonAuthHeaders } from '@/lib/qgammon/auth';
import { useGetMeQuery } from '@/services/qgammonApi';
import { useAppSelector } from '@/store/hooks';
import {
  expiryMsOf,
  expiryTimerDelay,
  initiatorOf,
  isRowOpen,
  seatsOf,
  type MatchRow,
} from '@/lib/qgammon/appointments';
import type { Player } from '@qlashgeminiz/qgammon-rules';

/**
 * QUELLO CHE IL GIOCATORE HA E NON SA DI AVERE — letto ALL'AVVIO, non nella lobby.
 *
 * ═══ PERCHE' L'ALTITUDINE E' IL DIFETTO, non il meccanismo ═══
 * Il recupero esisteva gia' (`useResumableMatch`) ma era montato dentro
 * `BackgammonLobby` ⇒ chi apre la Hub e **non entra nella sezione backgammon non
 * vede niente**, pur avendo una partita viva con i propri orologi che corrono.
 * ⚠️ E' un difetto **oggi**, col modello attuale: la decisione (c) di Luca — il
 * match non parte finche' il creatore non c'e' — non lo introduce, lo rende la
 * norma. ⇒ questa correzione e' vera in entrambi i mondi e non verra' riscritta
 * qualunque forma prenda il server.
 *
 * ═══ 🔴 IL GIUNTO ═══
 * ADR-0114 (`proposed`) prevede che l'accettazione di un invito produca un
 * APPUNTAMENTO e non un match, e che la lista diventi ETEROGENEA: partite vive e
 * appuntamenti insieme, distinti da `status: "waiting"`.
 * **IL CONTRATTO** [or49 (server) + or52 (ADR), concordato il 06/09 23:1xZ.
 * ⚠️ GRADO: trasportato da or35, **non verificato contro una rotta viva — la
 * rotta non esiste ancora**]:
 *   · `GET /matches` resta INVARIATO per difetto: solo le partite vive;
 *   · gli appuntamenti si chiedono con **`?include=waiting`**;
 *   · e arrivano con `status: "waiting"`, `expiresAt`, `players`, `createdBy`;
 *   · alla presenza di ENTRAMBI il match passa `waiting -> active` e il client
 *     riceve `match:state` sul socket, come oggi.
 * ⚠️ Mandare `?include=waiting` **oggi** e' innocuo: il server non dichiara uno
 * schema di query su quella rotta, quindi un parametro che non conosce lo ignora.
 * Il giorno che lo conosce, questa riga e' gia' quella giusta.
 * ⛔ Fino ad allora la lista che torna e' solo di partite vive: nessun
 * `status: "waiting"` puo' ancora arrivare, e il ramo che lo tratta non e'
 * esercitabile contro il server. E' cablato, non provato.
 *
 * ⚠️ [M 06/09 23:1xZ] `GET /matches` del gameserver **non legge il DB**: elenca le
 * sessioni in MEMORIA del registry. Oggi un appuntamento li' non comparirebbe
 * comunque, perche' il server non lo modella ancora.
 */

/** Cosa il giocatore ha in sospeso. Due stati, e non condividono una frase. */
export interface ResumableMatch {
  matchId: string;
  /** Il MIO colore: senza, il tabellone parte dal lato sbagliato. */
  perspective: Player;
  opponentName: string;
  /**
   * `live` = si sta giocando · `waiting` = qualcuno ti aspetta e NESSUNO gioca.
   *
   * 🔴 Non sono sfumature della stessa cosa: «Hai una partita in corso» su un
   * appuntamento e' **falso**, e sarebbe il primo appuntamento della storia
   * annunciato come una partita che non esiste. Due stati ⇒ due chiavi di copy,
   * mai un ternario dentro la frase.
   */
  kind: 'live' | 'waiting';
  /**
   * Quando l'appuntamento scade (epoch ms), o `null` su una partita viva.
   *
   * ⚠️ Serve PRIMA di tutto a non mostrarlo: v. il filtro nel provider. Che poi
   * la banda scriva o no quanto tempo resta e' una scelta di copy; che una riga
   * scaduta non compaia non lo e'.
   */
  expiresAt: number | null;
  /**
   * CHI HA CONIATO L'INVITO: `'me'` se l'appuntamento nasce da un mio invito che
   * qualcuno ha accettato, `'them'` se ho accettato io il suo, `'unknown'` se il
   * server non lo dice (riga vecchia, o partita viva).
   *
   * 🔴 PERCHE' QUESTO CAMPO E NON «CHI E' PRESENTE», che sarebbe la domanda
   * naturale. [M or56 2026-09-07, `qgammon-gameserver/src/game/payloads.ts:52`
   * @ origin/main] La riga di appuntamento porta i due posti attraverso
   * `identity()`, che espone `userId · displayName · avatarUrl · isBot · elo?`
   * — e **niente `connected`**, che invece la riga VIVA ha. ⇒ **il client non
   * puo' sapere chi dei due manca**, e una frase che lo presume («X ti sta
   * aspettando») e' vera solo per caso: finche' nessuno dei due e' entrato,
   * nessuno sta aspettando nessuno — l'appuntamento e' soltanto aperto.
   * ⇒ Si dice cio' che si SA (chi ha invitato chi, che e' un fatto compiuto) e
   * si aggiunge la regola che vale sempre: la partita inizia quando ci sono
   * entrambi. *Meglio una frase vaga e vera di una precisa e falsa.*
   */
  initiator: 'me' | 'them' | 'unknown';
}

/**
 * ⚠️ LE DUE FORME DI RIGA, `seatsOf`, il filtro sulla scadenza e la derivazione
 * di chi ha invitato chi stanno in `lib/qgammon/appointments.ts` — non per
 * ordine, ma perche' **un banco possa eseguirli**: un gate a
 * `node --experimental-strip-types` non importa un `.tsx`, e una regex sul
 * sorgente proverebbe che la riga e' scritta, non che decida bene. La prosa che
 * spiega ciascuna decisione vive li', accanto alla decisione.
 */

const ResumableMatchContext = createContext<ResumableMatch | null>(null);

/**
 * Monta il recupero ALL'AVVIO dell'app.
 *
 * 🔴 IL COSTO, dichiarato: qui la chiamata si paga a **ogni apertura dell'app**,
 * nel punto in cui una chiamata costa di piu', e per utenti che a backgammon non
 * giocano. L'unica condizione applicata e' `featureBackgammonEnabled`, e la
 * ragione per cui NON esclude nessuno in silenzio e' verificabile: col flag
 * spento le rotte `/backgammon/*` **non esistono nel router**, quindi non c'e'
 * nulla su cui atterrare — chi viene escluso non avrebbe potuto agire comunque.
 * ⛔ Le altre condizioni possibili (ruolo `beta`, differire dopo il primo
 * render) sono una DECISIONE DI PRODOTTO e non l'ho presa: le opzioni, col loro
 * costo e con **cosa smette di funzionare**, sono nel report.
 * ⚠️ Una condizione che restringe troppo ricrea in silenzio il difetto che
 * questo file chiude: chi viene escluso non riceve un errore, riceve niente.
 */
export function ResumableMatchProvider({ children }: { children: ReactNode }) {
  const [found, setFound] = useState<ResumableMatch | null>(null);
  /**
   * ═══ 🔴 LA LETTURA SI RIFA' QUANDO L'APP TORNA IN PRIMO PIANO ═══
   *
   * L'orologio piu' sotto copre **solo gli appuntamenti**: `expiryMsOf` rende
   * `null` se la riga non porta `expiresAt`, e **`expiresAt` lo espone soltanto
   * il ramo `waiting`** del gameserver [or49, 11/09]. ⇒ per una riga **`live`**
   * il timer non si arma mai, e le dipendenze della lettura
   * (`[enabled, myId, activeMatchId]`) **non cambiano col passare del tempo**:
   * la partita finisce, e la banda continua a offrirla.
   *
   * 🔴 Misurato l'11/09 su `stg-tma` (`or63`): la lobby offriva «Riprendi» per
   * una partita che il server **non aveva piu'** (`{"matches":[]}`), e il tocco
   * portava a una schermata che aspettava per sempre. `#245` ha chiuso il DANNO
   * (adesso quella schermata lo dice); **questo chiude la CAUSA.**
   *
   * ⛔ E NON e' un polling: si richiede **solo** quando il documento torna
   * visibile — lo stesso segnale che questa app usa gia' per rileggere i ruoli
   * (`visibilitychange`/`focus`). *Insistere contro un servizio che sta
   * rispondendo e' il difetto che `pollingBackoff` esiste per chiudere.*
   * ⚠️ `visibilitychange` scatta anche all'USCITA: si guarda
   * `visibilityState`, o si richiederebbe proprio mentre si va via.
   */
  const [giro, setGiro] = useState(0);
  const enabled = env.featureBackgammonEnabled;

  // `/auth/me` di qgammon-api da' l'id INTERNO (`publicUser.id`), lo stesso
  // spazio di id di `white.userId`/`black.userId` del gameserver: i due servizi
  // condividono la stessa tabella Prisma. ⛔ NON e' il `sub` di Logto —
  // `requireAuth` lo dichiara: «`sub` aliases the internal User.id».
  const { data: me } = useGetMeQuery(undefined, { skip: !enabled });
  const active = useAppSelector((s) => s.activeMatch.current);

  const myId = me?.user?.id ?? null;
  const activeMatchId = active?.matchId ?? null;

  useEffect(() => {
    const ritorno = () => {
      // ⚠️ `document.visibilityState` e non l'evento: `visibilitychange` scatta
      // in ENTRAMBI i versi, e richiedere mentre si esce e' lavoro buttato.
      if (document.visibilityState === 'visible') setGiro((n) => n + 1);
    };
    document.addEventListener('visibilitychange', ritorno);
    window.addEventListener('focus', ritorno);
    return () => {
      document.removeEventListener('visibilitychange', ritorno);
      window.removeEventListener('focus', ritorno);
    };
  }, []);

  useEffect(() => {
    if (!enabled || !myId) return;
    let cancelled = false;
    void (async () => {
      try {
        // ⬇️ IL GIUNTO, col nome del contratto — v. il blocco in testa al file.
        const res = await fetch(`${env.qgammonWsUrl}/matches?include=waiting`, {
          headers: await qgammonAuthHeaders(),
        });
        if (!res.ok || cancelled) return;
        const body = (await res.json()) as { matches?: MatchRow[] };
        if (cancelled) return;
        // Le DUE domande su una riga — «e' finita?» e «e' scaduta?» — stanno in
        // `isRowOpen`, che il banco esercita con date vere. Qui resta solo
        // l'istante a cui si guarda.
        const now = Date.now();
        const alive = (body.matches ?? []).filter((m) => isRowOpen(m, now));
        // Quella che sto gia' giocando non e' una scoperta.
        const candidate = alive.find((m) => m.matchId !== activeMatchId && seatsOf(m));
        const seats = candidate ? seatsOf(candidate) : null;
        if (!candidate || !seats) {
          setFound(null);
          return;
        }
        const iAmWhite = seats.W.userId === myId;
        setFound({
          matchId: candidate.matchId,
          perspective: iAmWhite ? 'W' : 'B',
          opponentName: iAmWhite ? seats.B.displayName : seats.W.displayName,
          // `waiting` lo dice il server sotto l'opt-in; qualunque altra cosa
          // (compreso `undefined`, che e' cio' che arriva senza opt-in) e' una
          // partita da riprendere.
          kind: candidate.status === 'waiting' ? 'waiting' : 'live',
          expiresAt: expiryMsOf(candidate),
          // `createdBy` assente ⇒ 'unknown', mai un ripiego su 'them': una
          // riga vecchia non e' un invito ricevuto, e dire quale dei due e'
          // stato senza saperlo e' la stessa presunzione che questo campo
          // esiste per togliere.
          initiator: initiatorOf(candidate, myId),
        });
      } catch {
        // Il gameserver muto non e' una notizia da dare: senza risposta non so
        // se una partita c'e', e affermare che non c'e' sarebbe un vuoto
        // affermato su una domanda mai andata a buon fine.
        if (!cancelled) setFound(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `giro` e' qui perche' la lettura si rifaccia al ritorno in primo piano: senza,
    // nessuna delle altre tre dipendenze cambia col passare del tempo.
  }, [enabled, myId, activeMatchId, giro]);

  /**
   * ═══ 🔴 L'OROLOGIO — la meta' di L-481 che il filtro qui sopra non poteva avere ═══
   * L'effetto della lettura ha `[enabled, myId, activeMatchId]` fra le dipendenze:
   * **nessuna delle tre cambia col passare del tempo**, quindi `isRowOpen` decide
   * una volta e non torna piu' a decidere. ⇒ un appuntamento scaduto PRIMA
   * dell'apertura non compariva gia' — ed e' cio' che il gate provava — ma uno che
   * scade **mentre l'app e' aperta restava sulla banda**, con un tocco che portava a
   * una partita morta. *Un filtro senza un istante in cui rifarlo e' una fotografia.*
   *
   * Sta nel PROVIDER e non nella banda di proposito: i consumatori di
   * `useResumableMatch()` sono gia' due (`ResumableBanner`, `BackgammonLobby`) e
   * quello che verra' domani non deve doversi ricordare di avere un orologio suo.
   * ⛔ E' la stessa ragione per cui la lettura e' salita in `AppShell`: mettere la
   * regola dove ciascuno se la copia e' il modo in cui uno dei due resta indietro.
   *
   * ⚠️ Lo scatto NON e' un verdetto: `expiryTimerDelay` tosa il ritardo a
   * `MAX_TIMER_MS`, quindi si **richiama** invece di credergli. `0` e' l'unica
   * risposta che vuol dire scaduto — e una data assurda costa un timer in piu'
   * invece di un ciclo caldo che riarma se stesso.
   */
  const expiresAt = found?.expiresAt ?? null;
  useEffect(() => {
    if (expiresAt === null) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const guarda = () => {
      const attesa = expiryTimerDelay(expiresAt, Date.now());
      if (attesa === null) return;
      if (attesa === 0) {
        setFound(null);
        return;
      }
      timer = setTimeout(guarda, attesa);
    };
    guarda();
    return () => {
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [expiresAt]);

  return (
    <ResumableMatchContext.Provider value={found}>{children}</ResumableMatchContext.Provider>
  );
}

/**
 * Cio' che il giocatore ha in sospeso, o `null`.
 *
 * ⚠️ Legge il contesto: NON fa una seconda chiamata. Prima questo hook faceva
 * lui la fetch, montato nella lobby — ed e' proprio quel montaggio a essere il
 * difetto. Chiamarlo fuori dal provider da' `null`, che e' il comportamento
 * giusto per una schermata che il provider non copre.
 */
export function useResumableMatch(): ResumableMatch | null {
  return useContext(ResumableMatchContext);
}
